import React, { useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	Link,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { isAdminOrSuperAdmin } from 'features/auth/helpers';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { useCreditAssignment, useCreditTerms } from '../../hooks/useCreditRules';
import {
	deleteCreditAssignment,
	extractCreditRulesError,
	getCreditAssignmentHistory,
	putCreditAssignment,
} from '../../services';
import { ICreditAssignment, ICreditTerm } from '../../types';
import { describeCreditTerm, formatFecha } from '../../helpers/describe';
import { condicionCreditoTooltip, dosEjesTooltip, vigenciaTooltip } from '../../constants';
import { CreditTermSummary } from '../CreditTermSummary';

// ─── Diálogo de asignación ───────────────────────────────────────────────────

interface AssignDialogProps {
	open: boolean;
	onClose: () => void;
	passengerId: string;
	passengerName?: string;
	/** Identidad para el snapshot cuando la API no puede resolver al cliente. */
	passengerDocument?: string;
	agencyId?: number;
	current: ICreditTerm | null;
	onSaved: () => void;
}

function AssignTermDialog({
	open,
	onClose,
	passengerId,
	passengerName,
	passengerDocument,
	agencyId,
	current,
	onSaved,
}: AssignDialogProps): JSX.Element {
	const { data: terms, loading: termsLoading } = useCreditTerms();
	const [termId, setTermId] = useState<number | ''>(current?.id ?? '');
	const [notes, setNotes] = useState('');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const selected = terms?.find((t) => t.id === termId) ?? null;

	const handleSave = async () => {
		if (termId === '') return;
		setSaving(true);
		setError(null);
		try {
			await putCreditAssignment(passengerId, {
				termId,
				notes: notes.trim() || undefined,
				// Solo se usa fuera de producción, cuando la API no encuentra al
				// cliente en su propia DB del Power (ambientes cruzados).
				passengerName,
				passengerDocument,
				agencyId,
			});
			onSaved();
			onClose();
		} catch (err) {
			setError(extractCreditRulesError(err));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
			<DialogTitle>{current ? 'Cambiar condición de crédito' : 'Asignar condición de crédito'}</DialogTitle>
			<DialogContent>
				<DialogContentText gutterBottom>
					{passengerName ? <strong>{passengerName}</strong> : 'Este cliente'} va a facturarse con la condición que
					elijas. Aplica a las facturas nuevas: las ya emitidas mantienen su vencimiento.
				</DialogContentText>

				<Stack spacing={2} mt={1}>
					<FormControl size="small" fullWidth disabled={termsLoading}>
						<InputLabel>Condición</InputLabel>
						<Select
							value={termId}
							label="Condición"
							onChange={(e) => {
								setTermId(Number(e.target.value));
								setError(null);
							}}
						>
							{(terms ?? []).map((t) => (
								<MenuItem key={t.id} value={t.id}>
									<Box>
										<Typography variant="body2" fontWeight={600}>
											{t.name}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{describeCreditTerm(t)}
										</Typography>
									</Box>
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{/* Preview: qué significa lo que está eligiendo, antes de guardar. */}
					{selected && (
						<Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50' }}>
							<Typography variant="caption" color="text.secondary" display="block" mb={1}>
								Cómo va a facturarse este cliente
							</Typography>
							<CreditTermSummary term={selected} />
						</Paper>
					)}

					<TextField
						label="Nota (opcional)"
						size="small"
						fullWidth
						multiline
						rows={2}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						placeholder="Ej: acordado con el cliente en la renovación del contrato"
						inputProps={{ maxLength: 500 }}
					/>

					{current && (
						<Alert severity="info" sx={{ py: 0.5 }}>
							La condición actual (<strong>{current.name}</strong>) queda registrada en el historial con su fecha de
							fin. No se pierde.
						</Alert>
					)}
					{error && <Alert severity="error">{error}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
					Cancelar
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={saving || termId === '' || termId === current?.id}
					sx={{ textTransform: 'none' }}
				>
					{saving ? <CircularProgress size={18} /> : 'Guardar'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

// ─── Historial ───────────────────────────────────────────────────────────────

function HistoryList({ passengerId }: { passengerId: string }): JSX.Element {
	const [rows, setRows] = useState<ICreditAssignment[] | null>(null);
	const [loading, setLoading] = useState(true);

	React.useEffect(() => {
		let cancelled = false;
		getCreditAssignmentHistory(passengerId)
			.then((h) => !cancelled && setRows(h))
			.catch(() => !cancelled && setRows([]))
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [passengerId]);

	if (loading) return <Skeleton variant="rectangular" height={60} />;
	if (!rows?.length) {
		return (
			<Typography variant="caption" color="text.secondary">
				No hay condiciones anteriores.
			</Typography>
		);
	}

	return (
		<Stack spacing={0.5}>
			{rows.map((h) => (
				<Box key={h.id} display="flex" alignItems="center" gap={1} flexWrap="wrap">
					<Chip
						label={h.termName ?? `#${h.termId}`}
						size="small"
						variant="outlined"
						color={h.vigente ? 'primary' : 'default'}
						sx={{ fontSize: 11 }}
					/>
					<Typography variant="caption" color="text.secondary">
						{formatFecha(h.vigenteDesde)} → {h.vigente ? 'hoy' : formatFecha(h.vigenteHasta)}
					</Typography>
					<Typography variant="caption" color="text.disabled">
						· {h.registeredBy}
					</Typography>
					{h.notes && (
						<Typography variant="caption" color="text.disabled" fontStyle="italic">
							· {h.notes}
						</Typography>
					)}
				</Box>
			))}
		</Stack>
	);
}

// ─── Tarjeta principal ───────────────────────────────────────────────────────

interface PassengerCreditTermProps {
	passengerId: string;
	passengerName?: string;
	/** Para el snapshot cuando la API no resuelve al cliente (ambientes cruzados). */
	passengerDocument?: string;
	agencyId?: number;
}

/**
 * Condición de crédito del cliente en su ficha: se ve siempre, y se edita con
 * el lápiz si tenés permiso. Es el lugar natural donde alguien que está mirando
 * al cliente espera encontrar (y corregir) su acuerdo de pago.
 */
export function PassengerCreditTerm({
	passengerId,
	passengerName,
	passengerDocument,
	agencyId,
}: PassengerCreditTermProps): JSX.Element {
	const user = useSelector((s: RootState) => s.auth.user, shallowEqual);
	const canEdit = isAdminOrSuperAdmin(user);
	const { data, loading, error, refetch } = useCreditAssignment(passengerId);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [showHistory, setShowHistory] = useState(false);
	const [removing, setRemoving] = useState(false);
	const [removeError, setRemoveError] = useState<string | null>(null);

	const term = data?.term ?? null;
	const assignment = data?.assignment ?? null;

	const handleRemove = async () => {
		setRemoving(true);
		setRemoveError(null);
		try {
			await deleteCreditAssignment(passengerId);
			refetch();
		} catch (err) {
			setRemoveError(extractCreditRulesError(err));
		} finally {
			setRemoving(false);
		}
	};

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
				<Box display="inline-flex" alignItems="center" gap={0.5}>
					<Typography variant="subtitle2" fontWeight={600}>
						Condición de crédito
					</Typography>
					<SectionInfoTooltip content={condicionCreditoTooltip} />
				</Box>
				{canEdit && !loading && (
					<Box display="flex" gap={0.5}>
						{term ? (
							<>
								<Tooltip title="Cambiar la condición de este cliente">
									<IconButton size="small" onClick={() => setDialogOpen(true)}>
										<EditIcon sx={{ fontSize: 17 }} />
									</IconButton>
								</Tooltip>
								<Tooltip title="Quitar la condición (el cliente queda sin acuerdo de pago)">
									<IconButton size="small" onClick={handleRemove} disabled={removing}>
										<DeleteOutlineIcon sx={{ fontSize: 17 }} />
									</IconButton>
								</Tooltip>
							</>
						) : (
							<Button
								size="small"
								variant="outlined"
								startIcon={<AddIcon sx={{ fontSize: 15 }} />}
								onClick={() => setDialogOpen(true)}
								sx={{ textTransform: 'none' }}
							>
								Asignar condición
							</Button>
						)}
					</Box>
				)}
			</Box>

			{loading ? (
				<Skeleton variant="rectangular" height={90} />
			) : error ? (
				<Alert severity="error">{error}</Alert>
			) : !term ? (
				<Alert severity="warning" sx={{ py: 0.75 }}>
					Este cliente <strong>no tiene condición de crédito</strong>. Al facturarle, el vencimiento se completa a mano.
					{!canEdit && ' Pedile a un administrador que le asigne una.'}
				</Alert>
			) : (
				<Paper variant="outlined" sx={{ p: 2 }}>
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
						<Box display="inline-flex" alignItems="center" gap={0.75}>
							<Typography variant="subtitle1" fontWeight={700} color="primary.main">
								{term.name}
							</Typography>
							<SectionInfoTooltip content={dosEjesTooltip} />
						</Box>
						{assignment?.vigenteDesde && (
							<Typography variant="caption" color="text.secondary">
								Vigente desde {formatFecha(assignment.vigenteDesde)}
							</Typography>
						)}
					</Box>

					<CreditTermSummary term={term} />

					{assignment?.notes && (
						<Box mt={1.5} p={1} bgcolor="grey.50" borderRadius={1}>
							<Typography variant="caption" color="text.secondary">
								{assignment.notes}
							</Typography>
						</Box>
					)}

					<Divider sx={{ my: 1.5 }} />
					<Box display="flex" alignItems="center" gap={0.5}>
						<Link
							component="button"
							type="button"
							variant="caption"
							underline="hover"
							onClick={() => setShowHistory((v) => !v)}
							sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}
						>
							<HistoryIcon sx={{ fontSize: 14 }} />
							{showHistory ? 'Ocultar historial' : 'Ver historial de condiciones'}
						</Link>
						<SectionInfoTooltip content={vigenciaTooltip} />
					</Box>
					<Collapse in={showHistory}>
						<Box mt={1}>
							<HistoryList passengerId={passengerId} />
						</Box>
					</Collapse>
				</Paper>
			)}

			{removeError && (
				<Alert severity="error" sx={{ mt: 1 }}>
					{removeError}
				</Alert>
			)}

			<AssignTermDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				passengerId={passengerId}
				passengerName={passengerName}
				passengerDocument={passengerDocument}
				agencyId={agencyId}
				current={term}
				onSaved={refetch}
			/>
		</Box>
	);
}
